import { ApolloError } from "@apollo/client";
import { Divider, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Auth } from "aws-amplify";
import { ErrorBox, ErrorBoxDialog } from "components/error";
import ConfirmGridActionDialog from "components/grid/ConfirmGridActionDialog";
import DataGridView from "components/grid/DataGridView";
import DeleteUserAction from "components/users/DeleteUserAction";
import Permissions from "components/users/Permissions";
import Roles from "components/users/Roles";
import { SolutionPermission } from "features/access-management";
import { UserName } from "features/solution-member";
import { TenantPermission } from "features/tenant";
import {
  ScopedRole__SolutionScope,
  ScopedRole__TenantScope
} from "model/schema/globalTypes";
import React, { useEffect } from "react";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    flex: 1,
    overflowY: "hidden"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  left: { display: "flex", alignItems: "center" },
  actions: { display: "flex", alignItems: "center" },
  button: {
    color: "#FFFFFF",
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.primary.dark,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  membersText: { fontSize: "1.25rem" },
  divider: {
    color: "#E4E6EF",
    height: 20,
    width: 2,
    margin: theme.spacing(0, 2)
  },
  membersCount: {
    color: "#7E8299",
    fontWeight: 500
  },
  skeletonText: {
    width: 200
  },
  skeletonRect: {
    width: "100%",
    height: 500
  },
  grid: { height: 400 },
  cancelButton: {
    textTransform: "capitalize"
  },
  removeButton: {
    textTransform: "capitalize",
    color: "#FFFFFF"
  },
  dialogText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  minWidth: { minWidth: 30 },
  cell: {
    display: "flex",
    alignItems: "center",
    height: "100%"
  },
  overflow: { overflow: "visible !important" },
  centered: {
    ".dx-datagrid-content .dx-datagrid-table .dx-row > td&": {
      verticalAlign: "middle"
    }
  }
}));

export interface User<T> {
  name: string;
  firstName: string | null;
  lastName: string | null;
  roles: T[];
  permissions?: (SolutionPermission | TenantPermission | null)[] | null;
}

export type RoleType = ScopedRole__SolutionScope | ScopedRole__TenantScope;

export interface UsersProps<T extends RoleType> {
  users: User<T>[];
  allRoles: T[];
  loadingRoles: boolean;
  onRoleUpdated: (userName: string, roles: T[]) => void;
  isLoading?: boolean;
  error?: ApolloError | ApolloError[];
  title?: string;
  onRefresh?: () => void;
  headerActions?: React.ReactNode;
  onRemoveUser?: (userName: string) => void;
  removeDialog?: React.ReactNode;
  solutionName?: string;
  removeUserError?: ApolloError | ApolloError[] | null;
  updateUserRoleError?: ApolloError | ApolloError[] | null;
  resetRemoveUserError: () => void;
  resetUpdateUserRoleError: () => void;
  loadUsers: () => void;
  lastAdminWarning?: string;
  adminRole?: T;
  adminCount?: number;
  disableDeleteUser?: boolean;
}

function Users<T extends RoleType>({
  users,
  allRoles,
  loadingRoles,
  onRoleUpdated,
  isLoading,
  error,
  title,
  onRefresh,
  headerActions,
  onRemoveUser,
  removeDialog,
  solutionName,
  removeUserError,
  updateUserRoleError,
  resetRemoveUserError,
  resetUpdateUserRoleError,
  loadUsers,
  lastAdminWarning,
  adminRole,
  adminCount,
  disableDeleteUser
}: UsersProps<T>) {
  const classNames = useStyles();
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = React.useState(false);
  const [userName, setUserName] = React.useState("");

  const handleCloseRemoveUserDialog = () => {
    setOpenRemoveUserDialog(false);
  };
  const [myUserName, setMyUserName] = React.useState<string>("");

  useEffect(() => {
    async function fetchUser() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setMyUserName(user?.username || "");
      } catch {
        setMyUserName("");
      }
    }
    fetchUser();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const mutationError = updateUserRoleError || removeUserError;

  return (
    <div className={classNames.root}>
      <div className={classNames.header}>
        <div className={classNames.left}>
          <h5 className={classNames.membersText}>{title}</h5>
          <Divider orientation="vertical" className={classNames.divider} />
          <Typography className={classNames.membersCount}>
            {users.length} Total
          </Typography>
        </div>
        <div className={classNames.actions}>
          {headerActions}
          {onRefresh && (
            <Button
              variant="contained"
              data-testid="refresh-button"
              className={classNames.button}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          )}
        </div>
        {openRemoveUserDialog ? (
          <ConfirmGridActionDialog
            message={`You are about to remove ${userName}`}
            onConfirm={() => onRemoveUser && onRemoveUser(userName)}
            open={openRemoveUserDialog}
            onClose={handleCloseRemoveUserDialog}
          />
        ) : null}
        <ErrorBoxDialog
          apolloError={mutationError}
          onClose={() => {
            handleCloseRemoveUserDialog();
            if (removeUserError) resetRemoveUserError();
            if (updateUserRoleError) resetUpdateUserRoleError();
          }}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <DataGridView<User<T>>
          dataSource={users}
          isLoading={isLoading}
          showFilterRow={false}
          allowSelection={false}
          height={error ? 35 : "100%"}
          onCellPrepared={e => {
            if (e.column.dataField === "roles") {
              e.cellElement.classList.add(classNames.overflow);
            }
          }}
          columns={[
            {
              colDef: {
                dataField: "name",
                caption: "Name",
                sortOrder: "asc",
                width: "auto"
              },
              render: p => <UserName data={p.data} />
            },
            {
              colDef: {
                dataField: "roles",
                caption: "Roles",
                cssClass: classNames.centered,
                allowSorting: false
              },
              render: ({ data }) => {
                return (
                  <Roles
                    userName={data.name}
                    myUserName={myUserName}
                    roles={data.roles}
                    allRoles={allRoles}
                    loadingRoles={loadingRoles}
                    onRoleUpdated={roles => onRoleUpdated(data.name, roles)}
                    lastAdminWarning={lastAdminWarning}
                    adminRole={adminRole}
                    adminCount={adminCount}
                  />
                );
              }
            },
            {
              colDef: {
                caption: "Actions",
                width: 130,
                allowFiltering: false,
                allowSorting: false
              },
              render: ({ data }) => {
                const permissionsValue = data.permissions || [];
                return (
                  <div className={classNames.cell}>
                    <Permissions
                      firstName={data.firstName}
                      lastName={data.lastName}
                      permissions={permissionsValue}
                    />
                    {onRemoveUser && !disableDeleteUser && (
                      <DeleteUserAction
                        data={data}
                        handleUserDeleteRequest={(userName: string) => {
                          setUserName(userName);
                          setOpenRemoveUserDialog(true);
                        }}
                        solutionName={solutionName}
                        myUserName={myUserName}
                        disabled={disableDeleteUser}
                      />
                    )}
                  </div>
                );
              }
            }
          ]}
        />
      </div>
      {removeDialog}
      {error ? <ErrorBox apolloError={error} /> : null}
    </div>
  );
}

export default Users;
